/**
 * The one place the WebCodecs worker's URL is resolved. Isolated because `import.meta.url`
 * is a Vite/ESM construct the CommonJS test build cannot parse; only the app composition
 * root (main.tsx) imports this module, and it hands the factory to the peer module.
 */
import type { WcWorkerPort } from '@playcastdotio/PeerCore';
import type { WebCodecsWorkerBootstrap } from './webcodecs.worker';
import { MainThreadWebCodecsPort } from './MainThreadWebCodecsPort';
import { selectWebCodecsExecutionMode } from './WebCodecsExecutionMode';

/** Spawn a fresh decode worker; PeerCore owns it from here (one per WebCodecs session). */
export function createWebCodecsWorker(): WcWorkerPort {
  if (selectWebCodecsExecutionMode(navigator.userAgent) === 'main-thread')
    return new MainThreadWebCodecsPort();
  const worker = new Worker(new URL('./webcodecs.worker.ts', import.meta.url), { type: 'module' });
  const bootstrap: WebCodecsWorkerBootstrap = { clockOriginMs: performance.timeOrigin };
  worker.postMessage(bootstrap);
  return {
    postMessage: (message, transfer = []) => worker.postMessage(message, transfer),
    onMessage: (listener) => {
      const receive = (event: MessageEvent) => listener(event.data);
      // A crashed or unparseable worker must surface as a session failure, not silence:
      // the bridge then sends `fallback` and the host restores conventional video.
      const failed = () => listener({ type: 'event', event: { type: 'failed', reason: 'WebCodecs worker failed' } });
      worker.addEventListener('message', receive);
      worker.addEventListener('error', failed);
      worker.addEventListener('messageerror', failed);
      return () => {
        worker.removeEventListener('message', receive);
        worker.removeEventListener('error', failed);
        worker.removeEventListener('messageerror', failed);
      };
    },
    terminate: () => worker.terminate(),
  };
}
